import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScrollText, Download, Lock, FileCheck2, ShieldCheck } from "lucide-react";
import { api, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FILTERS = [
  { id: "all", label: "All activity" },
  { id: "export", label: "Data exports" },
  { id: "erasure", label: "Erasures" },
  { id: "consent", label: "Consent" },
];

function classify(action = "") {
  const a = action.toLowerCase();
  if (a.includes("export")) return "export";
  if (a.includes("anonymis")) return "erasure";
  if (a.includes("consent")) return "consent";
  return "other";
}

const META = {
  export: { icon: Download, label: "Data export", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  erasure: { icon: Lock, label: "Erasure", cls: "bg-red-50 text-red-700 border-red-200" },
  consent: { icon: FileCheck2, label: "Consent", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  other: { icon: ShieldCheck, label: "Activity", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function GdprRegister() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!(user?.is_super || user?.role === "admin");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && !isAdmin) { navigate("/dashboard", { replace: true }); return; }
    if (!isAdmin) return;
    api.get("/gdpr/audit").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [user, isAdmin, navigate]);

  const rows = useMemo(
    () => items.map((i) => ({ ...i, kind: classify(i.action) })),
    [items],
  );
  const filtered = filter === "all" ? rows : rows.filter((r) => r.kind === filter);
  const counts = useMemo(() => {
    const c = { export: 0, erasure: 0, consent: 0 };
    rows.forEach((r) => { if (c[r.kind] !== undefined) c[r.kind] += 1; });
    return c;
  }, [rows]);

  if (!isAdmin) return null;

  return (
    <div className="space-y-6" data-testid="gdpr-register-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">GDPR Register</h1>
        <p className="mt-1 text-sm text-slate-500">
          Organisation-wide audit log of data exports, erasures and consent changes.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Total events" value={rows.length} icon={ScrollText} />
        <Stat label="Data exports" value={counts.export} icon={Download} />
        <Stat label="Erasures" value={counts.erasure} icon={Lock} />
        <Stat label="Consent changes" value={counts.consent} icon={FileCheck2} />
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`register-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === f.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3">Detail</th>
              <th className="px-4 py-3">Performed by</th>
              <th className="px-4 py-3">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((r) => {
              const m = META[r.kind] || META.other;
              const Icon = m.icon;
              return (
                <tr key={r.id} data-testid="register-row" className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${m.cls}`}>
                      <Icon className="h-3 w-3" /> {r.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800">{r.employee_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{r.detail || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{r.actor_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{shortDate(r.timestamp)}</td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No GDPR activity recorded yet.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">{label}</span></div>
      <p className="mt-2 font-heading text-2xl font-extrabold text-slate-900">{value}</p>
    </div>
  );
}
